import type { Emitter } from "@core/events/emitter";

/**
 * `IRightPanelUIModel` — web port of the visibility half of Unity's
 * `Models/IRightPanelUIModel.cs`, implemented on desktop by
 * `CampfireScenes/Assets/Campfire/Runtime/ViewModels/RightPanelViewModel.cs`.
 *
 * Owns whether the editor's RIGHT rail (inspector / comments) is on screen.
 * `IEditorUIModel` is the left-region half; the viewport context menu's
 * Show UI / Hide UI drives both (Unity `EntityContextView.OnShowUI` /
 * `OnHideUI`), and `ProjectView` binds the slide.
 *
 * **One flag where Unity has two, and why that is the same state.** Unity keeps
 * `IsInspectorVisible` and `IsCommentPanelVisible` as one mutually-exclusive
 * slot: both false is "rail hidden", and which one is true is the PAGE. The web
 * already keeps the page on `IAnnotationsModel.isPanelOpen` — the header
 * counter, the XR comments slate and the phone drawer all read it — so this
 * model owns only the other half, whether the slot is shown at all.
 *
 * Unity's restore memory survives the split: `HideAllExpandableUI` closes the
 * comments page and remembers it (`m_wasCommentPanelVisible`), and
 * `ShowAllExpandableUI` reopens it. Closing the page rather than leaving it
 * open behind a hidden rail is what keeps the header's comments button from
 * reading as pressed over nothing.
 *
 * **Not ported, deliberately:**
 *   - The auto-open latch (`m_isExpandableUIAutoOpenSuppressed`). Unity sets it
 *     on Hide UI so a selection, the comment tool, or a clicked marker does not
 *     re-open the panel it just hid. The web has none of those auto-opens yet:
 *     the rail opens only on an explicit request. A future auto-open (say,
 *     "select a part on the compact shell opens the inspector") must port the
 *     latch with it, or Hide UI will undo itself.
 *   - Add-action (`IsAddActionPanelVisible`, `ActionToEdit`,
 *     `DismissAddActionPanel`) — gated on the deprioritized scripts model.
 *   - `RightPanelReservedWidth` — the web's panels float over the viewport, so
 *     nothing reserves a width.
 *   - Persistence: plain fields upstream, plain fields here.
 */
export interface IRightPanelUIModel {
  /** Fires when {@link isOpen} changes. Payload is the new value. */
  readonly onChanged: Emitter<boolean>;

  /** Unity `IsInspectorVisible || IsCommentPanelVisible`. */
  readonly isOpen: boolean;

  /** Unity `IsAllUIHidden` — `!isOpen`. */
  readonly isAllUIHidden: boolean;

  /**
   * Show or fold the rail without touching the page. The compact shell and the
   * header's comments / inspector buttons use this; Show UI / Hide UI use the
   * two methods below, which also manage the page.
   */
  setOpen(open: boolean): void;

  /** Unity `ShowAllExpandableUI()` — opens the rail on the page it was hidden on. */
  showAllExpandableUI(): void;

  /** Unity `HideAllExpandableUI()` — folds the rail and remembers its page. */
  hideAllExpandableUI(): void;
}

export const RIGHT_PANEL_UI_MODEL = Symbol.for("Campfire.RightPanelUIModel");
